/**
 * IdentitySettings — Settings → Identity page.
 *
 * Shows the user's current email-verification state and a one-click
 * "Send verification email" CTA. Throttled server-side to 60s/user.
 *
 * Phone verification UI is intentionally absent until the phone-OTP
 * module ships (per user choice "c").
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, ShieldCheck, Phone, Loader2, CheckCircle2, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function IdentitySettings() {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/identity/status`, { withCredentials: true });
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load status");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/identity/email/send`, {}, { withCredentials: true });
      if (data.already_verified) {
        toast.info("You're already verified.");
      } else {
        toast.success(`Verification email sent to ${data.email}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send email");
    }
    setSending(false);
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Link to="/dashboard" data-testid="identity-back" className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </Link>
          <div>
            <h1 className="font-extrabold text-slate-900 dark:text-white">Identity</h1>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Confirm who you are. Power your trust badges.</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {loading && (
          <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 text-center text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline-block mr-1" /> Loading…
          </div>
        )}

        {!loading && status && (
          <>
            {/* Email row */}
            <section data-testid="identity-email-card" className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  status.email_verified
                    ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                    : "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"
                }`}>
                  <Mail className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-slate-900 dark:text-white">Email address</p>
                  <p className="text-[12px] text-slate-500 dark:text-slate-400 truncate">{status.email || "—"}</p>
                  {status.email_verified ? (
                    <div data-testid="identity-email-verified" className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold ring-1 ring-emerald-200/60">
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </div>
                  ) : (
                    <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-bold ring-1 ring-amber-200/60">
                      Not yet verified
                    </div>
                  )}
                </div>
              </div>

              {!status.email_verified && (
                <button
                  data-testid="identity-send-verify"
                  onClick={send}
                  disabled={sending}
                  className="mt-4 w-full h-11 rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-bold flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99] transition-transform"
                >
                  {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send verification email</>}
                </button>
              )}
              {status.pending_token && !status.email_verified && (
                <p data-testid="identity-pending-note" className="mt-2 text-[11.5px] text-slate-500 dark:text-slate-400 text-center">
                  We've sent a confirmation link — check your inbox. Click it to finish verification.
                </p>
              )}
            </section>

            {/* Phone row — placeholder until OTP module ships */}
            <section data-testid="identity-phone-card" className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 opacity-80">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-slate-900 dark:text-white">Phone number</p>
                  <p className="text-[12px] text-slate-500 dark:text-slate-400">Phone verification — coming soon</p>
                  <p className="mt-2 text-[11px] text-slate-400">
                    Once enabled, your Identity chip auto-upgrades from <strong>Email Verified</strong> to <strong>Identity Verified</strong>.
                  </p>
                </div>
              </div>
            </section>

            {/* Trust hint */}
            <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-3 flex items-start gap-2 text-[11.5px] text-slate-600 dark:text-slate-300">
              <ShieldCheck className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
              <p>
                Verifying your email lights up an <strong>Email Verified</strong> chip across the marketplace, your booking page, and pupil invites — the Trust Stack updates instantly.
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
